//
// renderer.ts — the map itself
//
// Translation of `Views/GleasonMapView.swift`, which was itself a port of
// the Fuser app's `GleasonCanvas.tsx`. So this is a raw
// CanvasRenderingContext2D render loop coming home to a raw
// CanvasRenderingContext2D render loop, by way of SwiftUI's `Canvas`.
//
// One Canvas draws everything, every frame, in this order: ocean, weather
// washes, continents, wind particles, terminator, parallels, meridians and
// labels, luminary paths, every contact, sun/moon markers, then the rim
// outside the clip. Reads state, draws, repeats.
//
// The wind particles live in a plain array that mutates without telling
// anyone — the same trick the Swift uses with `ParticleStore` and the React
// original used with `useRef`. Nothing re-renders on a particle moving.
//

import type { LayerKey, TrackedObject, WeatherParticle } from "../types";
import { CATEGORY_COLORS } from "../types";
import type { AppState } from "../state";
import { project, tracePath, traceTrack } from "./gleason";
import { MAP_LABELS, unitRings } from "./landmass";
import * as Solar from "../orbital/solar";
import { positionOf, trajectory } from "../orbital/satellite";
import { windAt } from "../api/era5";

/** Actual longitudes, placed with `project` so they cannot disagree with the land. */
const RIM_LONGITUDES: Array<[number, string]> = [
  [0, "0° Greenwich"], [30, "30°E"], [60, "60°E"], [90, "90°E"],
  [120, "120°E"], [150, "150°E"], [180, "180°"], [-150, "150°W"],
  [-120, "120°W"], [-90, "90°W"], [-60, "60°W"], [-30, "30°W"],
];

const LATITUDE_CIRCLES = [-80, -60, -30, 0, 30, 60, 80];

const GRID_LINE = "rgba(71, 84, 105, 0.35)";
const SPACE_BACKDROP = "rgb(5, 8, 18)";

export class GleasonRenderer {
  private ctx: CanvasRenderingContext2D;
  private particles: WeatherParticle[] = [];
  /** Where each contact landed last frame, for hit-testing taps. */
  private hitTargets: Array<{ obj: TrackedObject; x: number; y: number }> = [];

  private centerX = 0;
  private centerY = 0;
  private radius = 1;

  constructor(
    private canvas: HTMLCanvasElement,
    private state: AppState,
  ) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("AZISUR: 2D canvas context unavailable.");
    this.ctx = ctx;
    this.seedParticles();

    canvas.addEventListener("click", (event) => this.handleClick(event));
  }

  private seedParticles(count = 240): void {
    this.particles = Array.from({ length: count }, () => ({
      lat: (Math.random() - 0.5) * 150,
      lon: (Math.random() - 0.5) * 360,
      age: Math.random() * 100,
      maxAge: 80 + Math.random() * 80,
      speed: 0.2 + Math.random() * 0.4,
    }));
  }

  /**
   * Matches the backing store to the CSS size and the display's pixel
   * density. Without this the map is a blurry upscale on every retina
   * screen, which on a line-art instrument is very visible.
   */
  resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    this.canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /** One frame. Called from a requestAnimationFrame loop in main.ts. */
  render(): void {
    const ctx = this.ctx;
    const rect = this.canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    if (width < 2 || height < 2) return;

    const state = this.state;
    // Build 2.0, item 2: fill the short axis. Was 0.44, matching a square
    // SwiftUI view that boxed the Earth on iPad.
    const side = Math.min(width, height);
    this.radius = side * 0.48 * state.zoomScale;
    // Build 1.5, item 8: the arc's inner lane pans the disc. Offsets are a
    // fraction of the radius so the feel of a drag does not change with the
    // window size.
    this.centerX = width / 2 + state.panX * this.radius;
    this.centerY = height / 2 + state.panY * this.radius;

    const nowMs = Date.now() + state.timeOffsetHours * 3600 * 1000;

    ctx.fillStyle = SPACE_BACKDROP;
    ctx.fillRect(0, 0, width, height);

    // Everything from here to the rim is clipped to the Earth disc.
    ctx.save();
    ctx.beginPath();
    ctx.arc(this.centerX, this.centerY, this.radius, 0, Math.PI * 2);
    ctx.clip();

    this.drawOcean();
    this.drawCloudCover(nowMs);
    this.drawTempAnomaly();
    this.drawPrecipitation();
    this.drawContinents();
    this.drawWindParticles();
    this.drawTerminator(nowMs);
    this.drawParallels();
    this.drawMeridiansAndLabels();
    this.drawLuminaryPaths(nowMs);
    this.drawObjects(nowMs);
    this.drawSolarSystemMarkers();

    ctx.restore();

    this.drawRim();
  }

  // --- Layers -----------------------------------------------------------

  private drawOcean(): void {
    const ctx = this.ctx;
    const gradient = ctx.createRadialGradient(
      this.centerX, this.centerY, 0,
      this.centerX, this.centerY, this.radius,
    );
    gradient.addColorStop(0, "rgb(17, 31, 56)");
    gradient.addColorStop(0.5, "rgb(11, 22, 40)");
    gradient.addColorStop(0.85, "rgb(7, 15, 30)");
    gradient.addColorStop(1, "rgb(3, 7, 18)");
    ctx.fillStyle = gradient;
    ctx.fillRect(
      this.centerX - this.radius, this.centerY - this.radius,
      this.radius * 2, this.radius * 2,
    );
  }

  private drawERA5Canvas(canvas: HTMLCanvasElement | null, alpha: number): void {
    if (!canvas) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.drawImage(
      canvas,
      this.centerX - this.radius,
      this.centerY - this.radius,
      this.radius * 2,
      this.radius * 2,
    );
    ctx.restore();
  }

  private drawCloudCover(_nowMs: number): void {
    const { layers } = this.state;
    if (!layers.on.wetClouds) return;
    this.drawERA5Canvas(this.state.era5Cloud, layers.opacity.wetClouds / 100);
  }

  private drawTempAnomaly(): void {
    const { layers } = this.state;
    if (!layers.on.wetTemperature) return;
    this.drawERA5Canvas(this.state.era5Temp, layers.opacity.wetTemperature / 100);
  }

  private drawPrecipitation(): void {
    const { layers } = this.state;
    if (!layers.on.wetPrecipitation) return;
    this.drawERA5Canvas(this.state.era5Precip, layers.opacity.wetPrecipitation / 100);
  }

  private drawContinents(): void {
    const ctx = this.ctx;
    const { centerX, centerY, radius } = this;
    const zoom = this.state.zoomScale;
    ctx.fillStyle = "rgb(30, 46, 66)";
    ctx.strokeStyle = "rgb(56, 82, 112)";
    ctx.lineWidth = Math.max(0.6, 1.1 * Math.min(zoom, 2.5));
    ctx.beginPath();
    for (const ring of unitRings(zoom)) {
      const first = ring[0];
      ctx.moveTo(centerX + first.x * radius, centerY + first.y * radius);
      for (let i = 1; i < ring.length; i++) {
        ctx.lineTo(centerX + ring[i].x * radius, centerY + ring[i].y * radius);
      }
      ctx.closePath();
    }
    ctx.fill();
    ctx.stroke();
  }

  /** Seconds of real wind each animation frame represents. Direction is ERA5; only speed is exaggerated so it reads on a globe. */
  private static readonly windSecondsPerFrame = 2200;

  private drawWindParticles(): void {
    const { layers, era5 } = this.state;
    if (!layers.on.wetWind || !era5) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = layers.opacity.wetWind / 100;
    ctx.strokeStyle = "rgba(51, 212, 153, 0.7)";
    ctx.lineWidth = 1.2 * this.state.zoomScale;

    for (const p of this.particles) {
      p.age += p.speed;
      if (p.age > p.maxAge) {
        p.age = 0;
        p.lat = (Math.random() - 0.5) * 160;
        p.lon = (Math.random() - 0.5) * 360;
      }
      const { u, v } = windAt(era5, p.lat, p.lon);
      const dt = GleasonRenderer.windSecondsPerFrame * p.speed;
      p.lat += (v * dt) / 111000;
      const cosLat = Math.max(0.08, Math.abs(Math.cos((p.lat * Math.PI) / 180)));
      p.lon += (u * dt) / (111000 * cosLat);
      if (p.lat > 88) p.lat = 88;
      if (p.lat < -88) p.lat = -88;
      while (p.lon > 180) p.lon -= 360;
      while (p.lon < -180) p.lon += 360;

      const a = project(p.lat, p.lon, this.centerX, this.centerY, this.radius);
      const b = project(
        p.lat + (v * dt * 4) / 111000,
        p.lon + (u * dt * 4) / (111000 * cosLat),
        this.centerX, this.centerY, this.radius,
      );
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
    ctx.restore();
  }

  private drawTerminator(nowMs: number): void {
    if (!this.state.layers.on.satLuminaries) return;
    const ctx = this.ctx;
    const ring = Solar.terminatorRing(nowMs);
    tracePath(ctx, ring, this.centerX, this.centerY, this.radius, true);
    ctx.strokeStyle = "rgba(255, 217, 102, 0.35)";
    ctx.lineWidth = 1.2;
    ctx.stroke();
  }

  private drawParallels(): void {
    const { layers } = this.state;
    if (!layers.on.parallels) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = layers.opacity.parallels / 100;
    ctx.strokeStyle = GRID_LINE;
    ctx.lineWidth = 1;
    for (const lat of LATITUDE_CIRCLES) {
      const r = ((90 - lat) / 180) * this.radius;
      ctx.beginPath();
      ctx.arc(this.centerX, this.centerY, r, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }

  private drawMeridiansAndLabels(): void {
    const { layers, zoomScale } = this.state;
    if (!layers.on.graticule) return;
    const ctx = this.ctx;

    ctx.strokeStyle = GRID_LINE;
    ctx.lineWidth = 1;
    for (let lon = -180; lon < 180; lon += 30) {
      const rim = project(-90, lon, this.centerX, this.centerY, this.radius);
      ctx.beginPath();
      ctx.moveTo(this.centerX, this.centerY);
      ctx.lineTo(rim.x, rim.y);
      ctx.stroke();
    }

    const fontSize = Math.max(9, Math.round(10 * zoomScale));
    ctx.fillStyle = "rgba(203, 213, 227, 0.55)";
    ctx.font = `600 ${fontSize}px ui-monospace, SFMono-Regular, Menlo, monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (const label of MAP_LABELS) {
      const pt = project(label.lat, label.lon, this.centerX, this.centerY, this.radius);
      ctx.fillText(label.text, pt.x, pt.y);
    }
  }

  private drawLuminaryPaths(nowMs: number): void {
    const { layers } = this.state;
    if (!layers.on.orbitalTrails) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = layers.opacity.orbitalTrails / 100;
    ctx.setLineDash([1, 3]);
    ctx.lineWidth = 1.4;

    traceTrack(ctx, Solar.subsolarPath(nowMs), this.centerX, this.centerY, this.radius);
    ctx.strokeStyle = "rgba(255, 199, 51, 0.6)";
    ctx.stroke();

    traceTrack(ctx, Solar.sublunarPath(nowMs), this.centerX, this.centerY, this.radius);
    ctx.strokeStyle = "rgba(204, 212, 222, 0.6)";
    ctx.stroke();

    ctx.restore();
  }

  /**
   * Which second-tier button owns a contact.
   *
   * **Build 1.5, item 7.** This used to be two parallel switch statements
   * over `category`, and a third lived in the dial's catalogue. Now the
   * feed that fetched the contact tags it with its `layer`, and both
   * questions are one lookup in the same two maps the menu writes to — so
   * the map and the menu cannot disagree about who owns what.
   *
   * Places are furniture rather than a genre, and the sun and moon ride
   * with the luminaries seat.
   */
  private layerKeyOf(obj: TrackedObject): LayerKey | null {
    if (obj.layer) return obj.layer;
    if (obj.category === "place") return "places";
    if (obj.category === "subsolar" || obj.category === "sublunar") return "satLuminaries";
    return null;
  }

  private layerOpacityFor(obj: TrackedObject): number {
    const key = this.layerKeyOf(obj);
    return key ? this.state.layers.opacity[key] : 100;
  }

  private isVisible(obj: TrackedObject): boolean {
    const key = this.layerKeyOf(obj);
    // An untagged contact is a bug in whichever feed produced it, and is
    // better invisible than silently drawn under someone else's toggle.
    return key ? this.state.layers.on[key] : false;
  }

  /**
   * Where a contact is right now. Satellites propagate; aircraft
   * dead-reckon from their reported track and ground speed over the seconds
   * since their last fix; vessels use the original's slow drift; places
   * stay put.
   */
  private currentPositionOf(obj: TrackedObject, nowMs: number): { lat: number; lon: number } {
    const cat = obj.category;

    if (cat === "starlink" || cat === "active" || cat === "debris" ||
        cat === "rocket_body" || cat === "neo_hazardous" || cat === "neo_safe") {
      const pos = positionOf(obj, nowMs);
      return { lat: pos.lat, lon: pos.lon };
    }

    if (cat === "flight" || cat === "military_aircraft") {
      // These are real ADS-B fixes seconds old, so this is a short
      // dead-reckoning from real speed and track — not the original's
      // "advance by up to an hour of invented travel", which would fling a
      // real aircraft hundreds of miles off course.
      const headingRad = ((obj.headingDeg ?? 90) * Math.PI) / 180;
      const seconds = Math.min(Math.max(obj.positionAgeSeconds ?? 0, 0), 300);
      const knots = obj.speedKnots ?? 450;
      const nm = knots * (seconds / 3600);
      const dLat = (nm / 60) * Math.cos(headingRad);
      // Longitude degrees narrow toward the poles — a correction that
      // matters a great deal on a polar map.
      const cosLat = Math.max(Math.cos((obj.latitude * Math.PI) / 180), 0.01);
      const dLon = ((nm / 60) * Math.sin(headingRad)) / cosLat;
      return { lat: obj.latitude + dLat, lon: obj.longitude + dLon };
    }

    if (cat === "vessel") {
      const headingRad = ((obj.headingDeg ?? 90) * Math.PI) / 180;
      const dt = (nowMs / 1000) % 3600;
      return {
        lat: obj.latitude + Math.cos(headingRad) * dt * 0.00015,
        lon: obj.longitude + Math.sin(headingRad) * dt * 0.00025,
      };
    }

    return { lat: obj.latitude, lon: obj.longitude };
  }

  private drawObjects(nowMs: number): void {
    const ctx = this.ctx;
    const { zoomScale, layers } = this.state;
    const selectedId = this.state.selectedObject?.id;
    this.hitTargets = [];

    for (const obj of this.state.objects) {
      if (!this.isVisible(obj)) continue;

      const { lat, lon } = this.currentPositionOf(obj, nowMs);
      const pt = project(lat, lon, this.centerX, this.centerY, this.radius);
      const isSelected = selectedId === obj.id;
      const color = CATEGORY_COLORS[obj.category];
      const cat = obj.category;

      this.hitTargets.push({ obj, x: pt.x, y: pt.y });

      ctx.save();
      ctx.globalAlpha = this.layerOpacityFor(obj) / 100;

      // --- trail / corridor / wake ---
      const isSat = cat === "starlink" || cat === "active";
      if (isSat && layers.on.orbitalTrails && isSelected) {
        ctx.save();
        ctx.globalAlpha *= layers.opacity.orbitalTrails / 100;
        traceTrack(
          ctx, trajectory(obj, nowMs),
          this.centerX, this.centerY, this.radius,
        );
        ctx.strokeStyle = isSelected ? color : this.fade(color, 0.35);
        ctx.lineWidth = (isSelected ? 2.5 : 1) * zoomScale;
        ctx.stroke();
        ctx.restore();
      } else if (cat === "flight" || cat === "military_aircraft") {
        const headingRad = ((obj.headingDeg ?? 90) * Math.PI) / 180;
        const back = project(
          lat - Math.cos(headingRad) * 6, lon - Math.sin(headingRad) * 10,
          this.centerX, this.centerY, this.radius,
        );
        ctx.beginPath();
        ctx.moveTo(back.x, back.y);
        ctx.lineTo(pt.x, pt.y);
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = isSelected ? color : this.fade(color, 0.4);
        ctx.lineWidth = (isSelected ? 2 : 1) * zoomScale;
        ctx.stroke();
        ctx.setLineDash([]);
      } else if (cat === "vessel") {
        const headingRad = ((obj.headingDeg ?? 90) * Math.PI) / 180;
        const back = project(
          lat - Math.cos(headingRad) * 4, lon - Math.sin(headingRad) * 6,
          this.centerX, this.centerY, this.radius,
        );
        ctx.beginPath();
        ctx.moveTo(back.x, back.y);
        ctx.lineTo(pt.x, pt.y);
        ctx.strokeStyle = isSelected ? color : this.fade(color, 0.35);
        ctx.lineWidth = (isSelected ? 2 : 1) * zoomScale;
        ctx.stroke();
      }

      // --- marker ---
      // Build 2.0, item 1: a glow on every unselected Starlink read as a
      // second, fainter set of dots. Glow only the one you picked.
      ctx.shadowColor = color;
      ctx.shadowBlur = isSelected ? 14 : 0;
      ctx.fillStyle = color;
      ctx.strokeStyle = color;

      if (cat === "place") {
        // A surveyor's crosshair, not a dot — a place is a fixed reference,
        // and should not read as another moving contact among hundreds.
        const r = 5 * zoomScale;
        ctx.lineWidth = 1.2 * zoomScale;
        ctx.beginPath();
        ctx.moveTo(pt.x - r, pt.y); ctx.lineTo(pt.x + r, pt.y);
        ctx.moveTo(pt.x, pt.y - r); ctx.lineTo(pt.x, pt.y + r);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, r * 0.6, 0, Math.PI * 2);
        ctx.stroke();
      } else if (cat === "flight" || cat === "military_aircraft") {
        const angle = ((obj.headingDeg ?? 90) * Math.PI) / 180;
        ctx.save();
        ctx.translate(pt.x, pt.y);
        ctx.rotate(angle);
        ctx.beginPath();
        ctx.moveTo(0, -7 * zoomScale);
        ctx.lineTo(5 * zoomScale, 6 * zoomScale);
        ctx.lineTo(0, 3 * zoomScale);
        ctx.lineTo(-5 * zoomScale, 6 * zoomScale);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      } else if (cat === "vessel") {
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 3.5 * zoomScale, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, (isSelected ? 6 : 3) * zoomScale, 0, Math.PI * 2);
        ctx.fill();
      }

      if (isSelected) {
        ctx.shadowBlur = 0;
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 9 * zoomScale, 0, Math.PI * 2);
        ctx.strokeStyle = "white";
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      ctx.restore();
    }
  }

  private drawSolarSystemMarkers(): void {
    if (!this.state.layers.on.satLuminaries) return;
    const ctx = this.ctx;
    const { zoomScale } = this.state;
    const selectedId = this.state.selectedObject?.id;

    for (const obj of this.state.solarSystemObjects) {
      const pt = project(
        obj.latitude, obj.longitude,
        this.centerX, this.centerY, this.radius,
      );
      const isSun = obj.category === "subsolar";
      const color = CATEGORY_COLORS[obj.category];
      const isSelected = selectedId === obj.id;
      const r = (isSelected ? 8 : 5) * zoomScale;

      this.hitTargets.push({ obj, x: pt.x, y: pt.y });

      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = isSelected ? 14 : 8;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.font = `${10 * zoomScale}px ui-monospace, SFMono-Regular, Menlo, monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(isSun ? "☉" : "☾", pt.x, pt.y - r - 8);
      ctx.restore();
    }
  }

  private drawRim(): void {
    const ctx = this.ctx;
    ctx.strokeStyle = "rgb(51, 64, 84)";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(this.centerX, this.centerY, this.radius, 0, Math.PI * 2);
    ctx.stroke();

    ctx.strokeStyle = "rgb(99, 115, 140)";
    ctx.fillStyle = "rgb(99, 115, 140)";
    ctx.lineWidth = 1;
    ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    for (const [lon, label] of RIM_LONGITUDES) {
      const p1 = project(-90, lon, this.centerX, this.centerY, this.radius);
      const p2 = project(-90, lon, this.centerX, this.centerY, this.radius + 6);
      const pL = project(-90, lon, this.centerX, this.centerY, this.radius + 20);
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
      ctx.fillText(label, pL.x, pL.y);
    }
  }

  // --- Interaction ------------------------------------------------------

  /**
   * Mirrors `GleasonMapView.handleTap`: select the nearest contact within
   * 20 points, or clear the selection. Hit-testing reads the positions the
   * last frame actually drew, so what you tap is what you see — no
   * recomputation that could disagree with the picture.
   */
  private handleClick(event: MouseEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    let closest: TrackedObject | null = null;
    let minDistance = 20;

    for (const target of this.hitTargets) {
      const dist = Math.hypot(x - target.x, y - target.y);
      if (dist < minDistance) {
        minDistance = dist;
        closest = target.obj;
      }
    }

    this.state.selectObject(closest);
    if (closest) {
      this.state.showHUDFeedback(`[${closest.name.toUpperCase()}] SELECTED`);
    }
  }

  /** "rgb(r, g, b)" -> "rgba(r, g, b, alpha)". */
  private fade(rgb: string, alpha: number): string {
    return rgb.replace(/^rgb\(/, "rgba(").replace(/\)$/, `, ${alpha})`);
  }
}
